import { CheckCircle } from "@untitledui/icons"

import { cn } from "@/lib/utils"
import type { TrackPrompt } from "@/types"

import { suitFor } from "./conversation-suits"

interface ConversationCardProps {
  prompt: TrackPrompt
  /** Show the "your answer is in" affirmation chip. */
  affirm?: boolean
  /** Show the card order */
  order?: number
}

// The conversation card: a game piece, not an app card. Cream face on the
// composer's Manila well, playing-card anatomy mapped to the domain —
// slot position as the rank, content type as the suit, the prompt title
// as the question. Corner marks and pips are decoration (aria-hidden);
// their information travels through the deck's live region.
export function ConversationCard({
  prompt,
  affirm = false,
  order,
}: ConversationCardProps) {
  const suit = suitFor(prompt.content_type)

  return (
    <article className="bg-batyr-cream ring-batyr-ink/10 relative flex aspect-[5/7] flex-col items-center justify-center gap-3 overflow-hidden rounded-2xl px-6 py-14 text-center shadow-lg ring-1">
      <CornerMark
        position={order ?? prompt.position}
        suit={suit}
        className="top-3.5 left-3.5"
      />
      <CornerMark
        position={order ?? prompt.position}
        suit={suit}
        className="right-3.5 bottom-3.5 rotate-180"
      />

      <suit.icon aria-hidden className={cn("size-6", suit.color)} />

      <h3 className="text-batyr-navy xl:text-display-xs line-clamp-3 text-xl font-medium text-balance">
        {prompt.title}
      </h3>

      {prompt.guidance && (
        <p className="text-secondary line-clamp-4 text-sm text-pretty">
          {prompt.guidance}
        </p>
      )}

      <p className="text-tertiary absolute inset-x-0 bottom-6 text-xs font-medium">
        {affirm ? (
          <span className="bg-surface-affirm_tint text-batyr-stet-green inline-flex items-center gap-1 rounded-full px-2.5 py-1 font-medium">
            <CheckCircle aria-hidden className="size-3.5" />
            Your answer is in
          </span>
        ) : prompt.suggestions_count > 0 ? (
          `${prompt.suggestions_count} ${plural(prompt.suggestions_count, "answer")} so far`
        ) : (
          "No answers yet — yours starts it"
        )}
      </p>
    </article>
  )
}

function CornerMark({
  position,
  suit,
  className,
}: {
  position: number
  suit: ReturnType<typeof suitFor>
  className?: string
}) {
  return (
    <span
      aria-hidden
      className={cn(
        "absolute flex flex-col items-center gap-0.5",
        suit.color,
        className,
      )}
    >
      <span className="text-lg leading-none font-semibold tabular-nums">
        {position}
      </span>
      <suit.icon className="size-4" />
    </span>
  )
}

function plural(count: number, word: string) {
  return count === 1 ? word : `${word}s`
}
